import React from "react";

/**
 * <ProductImage /> — the ONLY component that renders a product image.
 *
 * Renders the product asset inside a fixed landscape container (default 3:2)
 * so vials, serum bottles, boxes and packaging all present at a consistent
 * scale. Optional per-product image controls (`image_fit`, `image_zoom`,
 * `image_offset_x`, `image_offset_y`) let admins fine-tune positioning
 * without regenerating the asset.
 *
 * Controls (all optional — sensible defaults kick in when missing):
 *   image_fit       — "contain" (default) | "cover" | "zoom-in" | "zoom-out"
 *                     • contain  → whole product visible, letterboxed edges
 *                     • cover    → crop to fill the frame
 *                     • zoom-in  → contain + 1.2× internal scale
 *                     • zoom-out → contain + 0.85× internal scale
 *   image_zoom      — 0.5-2.0 numeric override for the internal scale
 *   image_offset_x  — 0-100 (%) horizontal focal point (default 50)
 *   image_offset_y  — 0-100 (%) vertical focal point (default 50)
 *
 * Existing products render exactly as before when no controls are set.
 */

const FIT_PRESETS = {
    contain:    { objectFit: "contain", scale: 1 },
    cover:      { objectFit: "cover",   scale: 1 },
    "zoom-in":  { objectFit: "contain", scale: 1.2 },
    "zoom-out": { objectFit: "contain", scale: 0.85 },
};

export const ProductImage = ({
    product,
    ratio = "3 / 2",
    className = "",
    loading = "lazy",
    fetchPriority,
    onClick,
    hoverScale = false,
    children,
    // Override controls (admin preview). Fall back to product fields.
    fit,
    zoom,
    offsetX,
    offsetY,
}) => {
    const src = product?.image;
    const alt = product?.alt_text || product?.name || "Product";

    const fitKey = fit || product?.image_fit || "contain";
    const preset = FIT_PRESETS[fitKey] || FIT_PRESETS.contain;
    const scale = typeof zoom === "number" ? zoom : (product?.image_zoom ?? preset.scale);
    const ox = typeof offsetX === "number" ? offsetX : (product?.image_offset_x ?? 50);
    const oy = typeof offsetY === "number" ? offsetY : (product?.image_offset_y ?? 50);

    const hoverCls = hoverScale
        ? "transition-transform duration-700 group-hover:scale-[1.04]"
        : "";

    return (
        <div
            className={`relative w-full overflow-hidden ${className}`}
            style={{ aspectRatio: ratio }}
            onClick={onClick}
            data-testid={product?.slug ? `product-image-${product.slug}` : undefined}
        >
            {src ? (
                <img
                    src={src}
                    alt={alt}
                    loading={loading}
                    fetchPriority={fetchPriority}
                    decoding="async"
                    className={`absolute inset-0 w-full h-full select-none ${hoverCls}`}
                    style={{
                        objectFit: preset.objectFit,
                        objectPosition: `${ox}% ${oy}%`,
                        transform: scale !== 1 ? `scale(${scale})` : undefined,
                        transformOrigin: `${ox}% ${oy}%`,
                    }}
                    draggable="false"
                />
            ) : (
                <div className="absolute inset-0 grid place-items-center text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                    No image
                </div>
            )}
            {children}
        </div>
    );
};

export default ProductImage;
